// @flow
import React, { useState, useEffect } from 'react';
import './SearchFacets.scss';

type Props = {
  facetDistribution: Object,
  selectedFacets: Object,
  onFacetChange: (facet: string, value: string) => void,
  onSortChange: (sortBy: string) => void,
  currentSort: string,
  searchQuery: string,
};

const getFacetTitle = (facetKey: string): string => {
  const titleMap = {
    brand: '브랜드',
    categories: '카테고리',
    badges: '배지',
    tags: '태그',
    units: '단위',
    price: '가격대',
    likes: '좋아요',
    views: '조회수',
  };
  return titleMap[facetKey] || facetKey;
};

const SORT_OPTIONS = [
  { value: 'views:desc', label: '캐시바이 랭킹순' },
  { value: 'price:asc', label: '낮은가격순' },
  { value: 'price:desc', label: '높은가격순' },
  { value: 'title:asc', label: '이름순' },
  { value: 'created_at:desc', label: '최신순' },
];

const PRICE_RANGES = [
  { label: '10만원 이하', min: 0, max: 100000 },
  { label: '10만원 - 50만원', min: 100000, max: 500000 },
  { label: '50만원 - 100만원', min: 500000, max: 1000000 },
  { label: '100만원 이상', min: 1000000, max: null },
];

const SearchFacets = ({
  facetDistribution,
  selectedFacets,
  onFacetChange,
  onSortChange,
  currentSort,
  searchQuery,
}: Props) => {
  // 메인 검색 결과의 facetDistribution 사용
  const displayFacetData = facetDistribution;

  // 각 facet의 확장 상태 관리
  const [expandedFacets, setExpandedFacets] = useState({});

  // 각 facet의 검색어 관리
  const [facetSearchTerms, setFacetSearchTerms] = useState({});

  // 각 facet의 정렬 상태 관리 (count:desc, count:asc, name:asc, name:desc)
  const [facetSortModes, setFacetSortModes] = useState({});

  // 각 facet의 정렬 드롭다운 열림 상태 관리
  const [sortDropdownOpen, setSortDropdownOpen] = useState({});

  // 모바일 필터 패널 열림 상태 관리
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);

  // 커스텀 가격대 슬라이더 상태
  const [sliderMin, setSliderMin] = useState(0);
  const [sliderMax, setSliderMax] = useState(10000000);
  const [isDragging, setIsDragging] = useState(false);

  // selectedFacets 변경 시 facet 검색어 초기화
  useEffect(() => {
    setFacetSearchTerms({});
  }, [selectedFacets]);

  // 선택된 가격대를 슬라이더에 반영
  useEffect(() => {
    if (selectedFacets && selectedFacets.price) {
      const priceRange = selectedFacets.price;
      const [min, max] = priceRange.split('-');
      setSliderMin(parseInt(min, 10) || 0);
      if (max === 'max') {
        setSliderMax(10000000);
      } else {
        setSliderMax(parseInt(max, 10) || 10000000);
      }
    } else {
      // 가격대 선택 해제 시 초기화
      setSliderMin(0);
      setSliderMax(10000000);
    }
  }, [selectedFacets]);

  const handleFacetClick = (facetKey: string, value: string) => {
    // 가격대는 단일 선택
    if (facetKey === 'price') {
      const isCurrentlySelected = selectedFacets && selectedFacets[facetKey] === value;
      onFacetChange(facetKey, isCurrentlySelected ? '' : value);
      return;
    }

    // 다른 facet들은 멀티 선택
    const currentValues =
      selectedFacets && selectedFacets[facetKey]
        ? Array.isArray(selectedFacets[facetKey])
          ? selectedFacets[facetKey]
          : selectedFacets[facetKey].split(',').map(v => v.trim())
        : [];

    let newValues;
    if (currentValues.includes(value)) {
      // 이미 선택된 값이면 제거
      newValues = currentValues.filter(v => v !== value);
    } else {
      // 새로운 값 추가
      newValues = [...currentValues, value];
    }

    // 빈 배열이면 빈 문자열로, 아니면 쉼표로 연결
    const newValue = newValues.length === 0 ? '' : newValues.join(',');
    onFacetChange(facetKey, newValue);
  };

  const toggleFacetExpansion = (facetKey: string) => {
    setExpandedFacets(prev => ({
      ...prev,
      [facetKey]: !prev[facetKey],
    }));
  };

  const handleFacetSearch = (facetKey: string, searchTerm: string) => {
    setFacetSearchTerms(prev => ({
      ...prev,
      [facetKey]: searchTerm,
    }));
  };

  const toggleSortDropdown = (facetKey: string) => {
    setSortDropdownOpen(prev => ({
      ...prev,
      [facetKey]: !prev[facetKey],
    }));
  };

  const selectSortMode = (facetKey: string, mode: string) => {
    setFacetSortModes(prev => ({
      ...prev,
      [facetKey]: mode,
    }));
    setSortDropdownOpen(prev => ({
      ...prev,
      [facetKey]: false,
    }));
  };

  const getSortLabel = (facetKey: string) => {
    const mode = facetSortModes[facetKey] || 'count:desc';
    switch (mode) {
      case 'count:desc':
        return '개수 많은순';
      case 'count:asc':
        return '개수 적은순';
      case 'name:asc':
        return '이름 오름차순';
      case 'name:desc':
        return '이름 내림차순';
      default:
        return '개수 많은순';
    }
  };

  const sortOptions = [
    { value: 'count:desc', label: '개수 많은순' },
    { value: 'count:asc', label: '개수 적은순' },
    { value: 'name:asc', label: '이름 오름차순' },
    { value: 'name:desc', label: '이름 내림차순' },
  ];

  const renderSortOptions = () => (
    <div className="facet-group sort-group">
      <div className="facet-header">
        <h4 className="facet-title">정렬</h4>
      </div>
      <div className="facet-values">
        {SORT_OPTIONS.map(option => (
          <button
            key={option.value}
            type="button"
            className={`facet-item ${currentSort === option.value ? 'selected' : ''}`}
            onClick={() => onSortChange(option.value)}
          >
            <input
              type="radio"
              name="sort"
              value={option.value}
              checked={currentSort === option.value}
              onChange={() => {}}
              className="facet-radio"
            />
            <span className="facet-label">{option.label}</span>
          </button>
        ))}
      </div>
    </div>
  );

  const handleSliderChange = (type: 'min' | 'max', value: number) => {
    if (type === 'min') {
      setSliderMin(Math.min(value, sliderMax));
    } else {
      setSliderMax(Math.max(value, sliderMin));
    }
  };

  const handleSliderMouseUp = () => {
    setIsDragging(false);
    const rangeKey = sliderMax >= 10000000 ? `${sliderMin}-max` : `${sliderMin}-${sliderMax}`;
    handleFacetClick('price', rangeKey);
  };

  const formatPrice = (price: number): string => {
    if (price >= 10000000) return '1000만원+';
    if (price >= 10000) {
      const man = Math.floor(price / 10000);
      return `${man}만원`;
    }
    if (price === 0) return '0원';
    return `${price.toLocaleString()}원`;
  };

  const renderPriceRange = () => {
    // 현재 선택된 가격대가 커스텀인지 확인
    const currentPrice = selectedFacets && selectedFacets.price;
    const isCustomSelected = currentPrice && !PRICE_RANGES.some(range => 
      `${range.min}-${range.max || 'max'}` === currentPrice
    );

    return (
      <div className="facet-group price-range">
        <div className="facet-header">
          <h4 className="facet-title">가격대</h4>
        </div>
        <div className="facet-values">
          {PRICE_RANGES.map(range => {
            const rangeKey = `${range.min}-${range.max || 'max'}`;
            const isSelected = selectedFacets && selectedFacets.price === rangeKey;
            return (
              <button
                key={rangeKey}
                type="button"
                className={`facet-item ${isSelected ? 'selected' : ''}`}
                onClick={() => handleFacetClick('price', isSelected ? '' : rangeKey)}
              >
                <input
                  type="radio"
                  name="price"
                  checked={isSelected}
                  readOnly
                  className="facet-radio"
                />
                <span className="facet-label">{range.label}</span>
              </button>
            );
          })}
          
          <div className="custom-price-range">
            <div className="price-range-display">
              <span className="price-value">{formatPrice(sliderMin)}</span>
              <span className="price-separator">~</span>
              <span className="price-value">{formatPrice(sliderMax)}</span>
            </div>
            
            <div className="range-slider-container">
              <input
                type="range"
                min="0"
                max="10000000"
                step="10000"
                value={sliderMin}
                onChange={(e) => handleSliderChange('min', parseInt(e.target.value, 10))}
                onMouseDown={() => setIsDragging(true)}
                onMouseUp={handleSliderMouseUp}
                onTouchEnd={handleSliderMouseUp}
                className="range-slider range-slider-min"
              />
              <input
                type="range"
                min="0"
                max="10000000"
                step="10000"
                value={sliderMax}
                onChange={(e) => handleSliderChange('max', parseInt(e.target.value, 10))}
                onMouseDown={() => setIsDragging(true)}
                onMouseUp={handleSliderMouseUp}
                onTouchEnd={handleSliderMouseUp}
                className="range-slider range-slider-max"
              />
              <div className="range-track">
                <div 
                  className="range-track-active"
                  style={{
                    left: `${(sliderMin / 10000000) * 100}%`,
                    width: `${((sliderMax - sliderMin) / 10000000) * 100}%`
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderFacetGroup = (facetKey: string, facetValues: Object) => {
    const facetTitle = getFacetTitle(facetKey);

    // 배열 형태의 facet 값들 처리 (categories, tags 등)
    const processedValues = {};

    if (Array.isArray(facetValues)) {
      facetValues.forEach(item => {
        if (typeof item === 'string') {
          processedValues[item] = (processedValues[item] || 0) + 1;
        } else if (Array.isArray(item)) {
          item.forEach(subItem => {
            processedValues[subItem] = (processedValues[subItem] || 0) + 1;
          });
        }
      });
    } else {
      Object.assign(processedValues, facetValues);
    }

    let allEntries = Object.entries(processedValues);

    // 검색어 필터링
    const searchTerm = facetSearchTerms[facetKey] || '';
    if (searchTerm.trim()) {
      allEntries = allEntries.filter(([value]) =>
        value.toLowerCase().includes(searchTerm.toLowerCase()),
      );
    }

    // 선택된 항목 확인 함수
    const isValueSelected = (value) => {
      if (facetKey === 'price') {
        return selectedFacets && selectedFacets[facetKey] === value;
      }
      const currentValues =
        selectedFacets && selectedFacets[facetKey]
          ? Array.isArray(selectedFacets[facetKey])
            ? selectedFacets[facetKey]
            : selectedFacets[facetKey].split(',').map(v => v.trim())
          : [];
      return currentValues.includes(value);
    };

    // 정렬 적용
    const sortMode = facetSortModes[facetKey] || 'count:desc';
    allEntries = allEntries.sort(([nameA, countA], [nameB, countB]) => {
      const isSelectedA = isValueSelected(nameA);
      const isSelectedB = isValueSelected(nameB);

      // 선택된 항목을 맨 위로
      if (isSelectedA && !isSelectedB) return -1;
      if (!isSelectedA && isSelectedB) return 1;

      // 둘 다 선택되었거나 둘 다 선택되지 않은 경우 정렬 모드 적용
      switch (sortMode) {
        case 'count:desc':
          return countB - countA;
        case 'count:asc':
          return countA - countB;
        case 'name:asc':
          return nameA.localeCompare(nameB);
        case 'name:desc':
          return nameB.localeCompare(nameA);
        default:
          return countB - countA;
      }
    });

    if (allEntries.length === 0) {
      return (
        <div key={facetKey} className="facet-group">
          <div className="facet-header">
            <h4 className="facet-title">{facetTitle}</h4>
            <div className="facet-controls">
              <div className="sort-dropdown">
                <button
                  className="facet-sort-btn"
                  onClick={() => toggleSortDropdown(facetKey)}
                  title="정렬 방식 선택"
                >
                  {getSortLabel(facetKey)} ▼
                </button>
                {sortDropdownOpen[facetKey] && (
                  <div className="sort-dropdown-menu">
                    {sortOptions.map(option => (
                      <button
                        key={option.value}
                        className={`sort-option ${
                          facetSortModes[facetKey] === option.value ? 'selected' : ''
                        }`}
                        onClick={() => selectSortMode(facetKey, option.value)}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="facet-search">
            <input
              type="text"
              placeholder={`${facetTitle} 검색...`}
              value={searchTerm}
              onChange={e => handleFacetSearch(facetKey, e.target.value)}
              className="facet-search-input"
            />
          </div>
          <div className="facet-values">
            <div className="no-results">검색 결과 없음</div>
          </div>
        </div>
      );
    }

    const isExpanded = expandedFacets[facetKey];
    const defaultLimit = facetKey === 'categories' ? 10 : 5;
    const displayEntries = isExpanded ? allEntries : allEntries.slice(0, defaultLimit);
    const hasMore = allEntries.length > defaultLimit;

    return (
      <div key={facetKey} className="facet-group">
        <div className="facet-header">
          <h4 className="facet-title">{facetTitle}</h4>
          <div className="facet-controls">
            <div className="sort-dropdown">
              <button
                className="facet-sort-btn"
                onClick={() => toggleSortDropdown(facetKey)}
                title="정렬 방식 선택"
              >
                {getSortLabel(facetKey)} ▼
              </button>
              {sortDropdownOpen[facetKey] && (
                <div className="sort-dropdown-menu">
                  {sortOptions.map(option => (
                    <button
                      key={option.value}
                      className={`sort-option ${
                        facetSortModes[facetKey] === option.value ? 'selected' : ''
                      }`}
                      onClick={() => selectSortMode(facetKey, option.value)}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="facet-search">
          <input
            type="text"
            placeholder={`${facetTitle} 검색...`}
            value={searchTerm}
            onChange={e => handleFacetSearch(facetKey, e.target.value)}
            className="facet-search-input"
          />
        </div>
        <div className="facet-values">
          {displayEntries.map(([value, count]) => {
            let isSelected;

            if (facetKey === 'price') {
              // 가격대는 단일 선택
              isSelected = selectedFacets && selectedFacets[facetKey] === value;
            } else {
              // 다른 facet들은 멀티 선택
              const currentValues =
                selectedFacets && selectedFacets[facetKey]
                  ? Array.isArray(selectedFacets[facetKey])
                    ? selectedFacets[facetKey]
                    : selectedFacets[facetKey].split(',').map(v => v.trim())
                  : [];
              isSelected = currentValues.includes(value);
            }

            return (
              <label key={value} className={`facet-item ${isSelected ? 'selected' : ''}`}>
                <input
                  type={facetKey === 'price' ? 'radio' : 'checkbox'}
                  name={facetKey === 'price' ? 'price' : undefined}
                  checked={isSelected}
                  onChange={() => handleFacetClick(facetKey, value)}
                  className={facetKey === 'price' ? 'facet-radio' : 'facet-checkbox'}
                />
                <span className="facet-label">{value}</span>
                <span className="facet-count">({count})</span>
              </label>
            );
          })}
          {hasMore && (
            <button className="facet-toggle" onClick={() => toggleFacetExpansion(facetKey)}>
              {isExpanded ? '접기' : `더보기 (+${allEntries.length - defaultLimit})`}
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="SearchFacets">
      <div className="mobile-filter-toggle">
        <button
          className="mobile-filter-btn"
          onClick={() => setIsMobileFilterOpen(!isMobileFilterOpen)}
        >
          필터 {isMobileFilterOpen ? '닫기' : '열기'}
        </button>
      </div>

      <div className={`facets-panel ${isMobileFilterOpen ? 'open' : ''}`}>
        {isMobileFilterOpen && (
          <div className="modal-backdrop" onClick={() => setIsMobileFilterOpen(false)} />
        )}
        <div className="facets-content">
          <div className="facets-header">
            <h3>필터 및 정렬</h3>
            <div className="header-actions">
              {selectedFacets && Object.keys(selectedFacets).length > 0 && (
                <button
                  className="clear-filters"
                  onClick={() => {
                    Object.keys(selectedFacets).forEach(key => {
                      onFacetChange(key, '');
                    });
                  }}
                >
                  전체 해제
                </button>
              )}
              <button className="close-modal" onClick={() => setIsMobileFilterOpen(false)}>
                ✕
              </button>
            </div>
          </div>

          <div className="facets-body">
            {renderSortOptions()}

            {displayFacetData && (() => {
              const entries = Object.entries(displayFacetData).filter(([key]) => key !== 'price');
              const facetOrder = ['categories', 'brand', 'badges', 'tags', 'units'];
              
              // facetOrder에 따라 정렬
              const sortedEntries = entries.sort(([keyA], [keyB]) => {
                const indexA = facetOrder.indexOf(keyA);
                const indexB = facetOrder.indexOf(keyB);
                
                if (indexA === -1 && indexB === -1) return 0;
                if (indexA === -1) return 1;
                if (indexB === -1) return -1;
                
                return indexA - indexB;
              });
              
              return sortedEntries.map(([facetKey, facetValues]) => 
                renderFacetGroup(facetKey, facetValues)
              );
            })()}

            {renderPriceRange()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchFacets;



// WEBPACK FOOTER //
// ./src/components/search/SearchFacets/SearchFacets.js